import React, { useMemo } from 'react'
import { DollarSign, BarChart3, TrendingUp, Layers, Loader2 } from 'lucide-react'
import { KPICard } from '../ui'
import { Cell } from './mockData'

export interface RawCell { content_category: string; post_type: string; post_count: number; total_emv: number }
interface Row { key: string; label: string; cells: Record<string, Cell>; total: Cell }

const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(2)}K` : `$${(n || 0).toFixed(2)}`
const fmtNum = (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n ?? 0}`)

// Raw post_type → friendly format bucket (matches the Posts page vocabulary).
const formatBucket = (t: string): string => {
  const k = (t || '').toLowerCase()
  if (['sidecar', 'graphsidecar', 'carousel'].includes(k)) return 'Carousel'
  if (['reel', 'reels', 'clips'].includes(k)) return 'Reel'
  if (['video', 'graphvideo'].includes(k)) return 'Video'
  if (['image', 'graphimage', 'photo', 'post'].includes(k)) return 'Image'
  if (k === 'story') return 'Story'
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : 'Other'
}
const catLabel = (k: string): string =>
  k === 'other' ? 'Other' : k === 'unclassified' ? 'Unclassified'
    : (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const GREEN_LOW = [240, 253, 244]
const GREEN_HIGH = [4, 120, 87]
const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t)
const cellShade = (emv: number, max: number): { bg: string; color: string } => {
  const t = Math.min(1, Math.max(0, emv / max))
  const rgb = GREEN_LOW.map((c, i) => lerp(c, GREEN_HIGH[i]!, t))
  return { bg: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`, color: t > 0.5 ? '#ffffff' : '#065f46' }
}
// Unclassified posts (in social_posts but not yet enriched by scout) shade GREY, not green —
// they read as a coverage gap, not a performance signal. This bucket shrinks as scout ingestion
// catches up. Uses its own scale so it never skews the green EMV shading.
const GREY_LOW = [243, 244, 246]
const GREY_HIGH = [75, 85, 99]
const cellShadeGrey = (emv: number, max: number): { bg: string; color: string } => {
  const t = Math.min(1, Math.max(0, emv / max))
  const rgb = GREY_LOW.map((c, i) => lerp(c, GREY_HIGH[i]!, t))
  return { bg: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`, color: t > 0.5 ? '#ffffff' : '#374151' }
}

interface Props {
  cells: RawCell[]
  loading: boolean
  error?: string | null
  onCellClick: (categoryKey: string, categoryLabel: string, format: string, cell: Cell) => void
}

const ContentHeatmap: React.FC<Props> = ({ cells, loading, error, onCellClick }) => {
  const { formats, rows, maxCell, kpi } = useMemo(() => {
    const catMap = new Map<string, Map<string, Cell>>()
    const fmtTotals = new Map<string, number>()
    for (const c of cells) {
      const cat = c.content_category || 'other'
      const fmt = formatBucket(c.post_type)
      if (!catMap.has(cat)) catMap.set(cat, new Map())
      const m = catMap.get(cat)!
      const cur = m.get(fmt) || { emv: 0, posts: 0 }
      cur.emv += c.total_emv; cur.posts += c.post_count
      m.set(fmt, cur)
      fmtTotals.set(fmt, (fmtTotals.get(fmt) || 0) + c.total_emv)
    }
    const formats = [...fmtTotals.entries()].sort((a, b) => b[1] - a[1]).map((e) => e[0])
    let maxCell = 1, totalEmv = 0, totalPosts = 0
    const rows: Row[] = [...catMap.entries()].map(([cat, m]) => {
      const cells2: Record<string, Cell> = {}
      let rEmv = 0, rPosts = 0
      for (const f of formats) {
        const cell = m.get(f) || { emv: 0, posts: 0 }
        cells2[f] = cell; rEmv += cell.emv; rPosts += cell.posts
        if (cat !== 'unclassified' && cell.emv > maxCell) maxCell = cell.emv
      }
      totalEmv += rEmv; totalPosts += rPosts
      return { key: cat, label: catLabel(cat), cells: cells2, total: { emv: rEmv, posts: rPosts } }
    }).sort((a, b) => {
      // Unclassified always sinks to the bottom, regardless of its EMV.
      if (a.key === 'unclassified') return 1
      if (b.key === 'unclassified') return -1
      return b.total.emv - a.total.emv
    })
    return { formats, rows, maxCell, kpi: { totalEmv, totalPosts, avgEmv: totalPosts ? totalEmv / totalPosts : 0, categories: rows.filter((r) => r.total.posts > 0 && r.key !== 'unclassified').length } }
  }, [cells])

  const gridTemplate = `minmax(150px, 1.5fr) repeat(${Math.max(formats.length, 1)}, minmax(88px, 1fr)) minmax(116px, 0.9fr)`

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total EMV" value={loading ? '—' : fmtEMV(kpi.totalEmv)} icon={DollarSign} colorScheme="emerald" />
        <KPICard label="Posts Analyzed" value={loading ? '—' : fmtNum(kpi.totalPosts)} icon={BarChart3} colorScheme="purple" />
        <KPICard label="Avg EMV / Post" value={loading ? '—' : fmtEMV(kpi.avgEmv)} icon={TrendingUp} colorScheme="amber" />
        <KPICard label="Content Categories" value={loading ? '—' : String(kpi.categories)} icon={Layers} colorScheme="blue" />
      </div>

      {/* Heatmap */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center min-h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : error ? (
          <div className="min-h-[200px] flex items-center justify-center text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="min-h-[200px] flex items-center justify-center text-sm text-gray-400">No content data for the selected filters.</div>
        ) : (
          <div className="min-w-[720px] max-w-7xl mx-auto">
            <div className="grid items-end gap-1.5 mb-1.5" style={{ gridTemplateColumns: gridTemplate }}>
              <div />
              {formats.map((f) => <div key={f} className="text-center text-xs font-semibold text-gray-600 pb-1">{f}</div>)}
              <div className="text-center text-xs font-semibold text-gray-600 pb-1">Total</div>
            </div>
            <div className="space-y-1.5">
              {rows.map((row) => {
                const isUnc = row.key === 'unclassified'
                return (
                <div key={row.key} className="grid items-stretch gap-1.5" style={{ gridTemplateColumns: gridTemplate }}>
                  <div className={`flex items-center justify-end pr-2 text-right text-xs font-medium ${isUnc ? 'text-gray-400 italic' : 'text-gray-800'}`}>{row.label}</div>
                  {formats.map((f) => {
                    const c = row.cells[f] || { emv: 0, posts: 0 }
                    const empty = c.posts <= 0
                    const shade = isUnc ? cellShadeGrey(c.emv, maxCell) : cellShade(c.emv, maxCell)
                    const avg = c.posts ? c.emv / c.posts : 0
                    return (
                      <button
                        key={f}
                        onClick={() => onCellClick(row.key, row.label, f, c)}
                        disabled={empty}
                        className={`rounded-lg px-2 py-2.5 text-center transition-transform ${empty ? 'border border-dashed border-gray-200 cursor-default' : 'hover:scale-[1.03] cursor-pointer'}`}
                        style={empty ? {} : { backgroundColor: shade.bg, color: shade.color }}
                      >
                        {empty ? <span className="text-gray-300 text-sm">—</span> : (
                          <>
                            <div className="text-lg font-bold leading-none">{fmtEMV(c.emv)}</div>
                            <div className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] font-medium leading-none opacity-75">
                              <span>{c.posts} posts</span>
                              <span className="opacity-40">•</span>
                              <span>{fmtEMV(avg)} avg</span>
                            </div>
                          </>
                        )}
                      </button>
                    )
                  })}
                  <div className="rounded-lg px-2 py-2.5 text-center bg-slate-700 text-white">
                    <div className="text-lg font-bold leading-none">{fmtEMV(row.total.emv)}</div>
                    <div className="mt-1.5 text-[10px] font-medium leading-none opacity-75">{row.total.posts} posts</div>
                  </div>
                </div>
                )
              })}
            </div>
            <div className="flex items-center gap-2 mt-4 text-[10px] text-gray-500">
              <span>Lower total EMV</span>
              <div className="flex gap-0.5">
                {[0, 0.25, 0.5, 0.75, 1].map((t) => {
                  const rgb = GREEN_LOW.map((c, i) => lerp(c, GREEN_HIGH[i]!, t))
                  return <span key={t} className="h-3 w-6 rounded-sm border border-gray-200" style={{ backgroundColor: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` }} />
                })}
              </div>
              <span>Higher</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContentHeatmap
